import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminApi,
  type FareCategory,
  type FareRateBlock,
  type FareRateInput,
} from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, StatTile, type Column } from '@/components/ui';
import { IconReceipt, IconCar } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader } from '@/components/admin/shared';
import { Drawer, Field } from '@/components/admin/Drawer';
import { useAdminPermission } from '@/lib/useAdminPermission';

/** Fare Rates — the per-category (trip-type) per-km rate table, porting the
 *  legacy Filament FareRateResource ("Per-category rates" under Fare Management).
 *
 *  WHAT IT SHOWS (mirrors the legacy resource's columns exactly): one ROW PER
 *  trip type (Premier / Logistics / Retail / Reserve), each carrying the
 *  time-banded CUSTOMER per-km rates (Day 06–18 / Evening 18–24 / Night 00–06)
 *  and the DRIVER per-km rates (Day / Evening / Night + the flat sub-contractor
 *  rate). Above the table sit the headline km-credit knobs — the buying rate the
 *  wallet + trip charge use, the platform commission, and the product-level
 *  surge/fleet facts.
 *
 *  EDITABLE per category (data.editable, gated by fares.manage — finance +
 *  superadmin) once admin-svc's per-category `fare_rates` table (migration 036)
 *  is present: an Edit button opens a drawer to set that category's customer +
 *  driver time-banded per-km rates. admin-svc owns + audits the write directly
 *  (the table has no other owning Go service; trip-svc's fare calc only READS
 *  it, falling back per-category → global → default).
 *
 *  GLOBAL + READ-ONLY (NOT editable here): the km-credit BUYING RATE + the admin
 *  COMMISSION live on the single trip_settings row, owned + written by trip-svc.
 *  They're shown as read-only StatTiles; editing them needs a trip-svc write
 *  path (a separate follow-up). The drawer never exposes them.
 *
 *  HONEST FALLBACK: when `fare_rates` isn't applied yet (data.editable=false),
 *  the page is read-only and every category shows the shared global block —
 *  with a precise note (data.editable_blocked_reason) instead of a dead button.
 *
 *  DEFENSIVE: the endpoint fails soft — an absent/unseeded trip_settings degrades
 *  to a clean "rates not configured" state, and an absent trip_types catalogue
 *  falls back to the four business-model categories, so the page always renders.
 *
 *  Gated by fares.read (held by every staff role today) for the table. */
export function FareRates() {
  // null = closed; a FareCategory = edit that category's rates.
  const [editing, setEditing] = useState<FareCategory | null>(null);
  const canManage = useAdminPermission('fares.manage');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'fare-rates'],
    queryFn: ({ signal }) => adminApi.fareRates(signal),
  });

  const win = data?.time_windows;
  // The per-category edit is offered only when the server says the table is
  // editable (fare_rates present) AND the staffer holds fares.manage. The server
  // enforces the same gate + 503s if the table is missing, so this is a UX gate.
  const canEdit = Boolean(data?.editable) && canManage;

  return (
    <AdminPage>
      <AdminPageHeader
        title="Fare Rates"
        subtitle="Per-category per-km rates by time window — the km-credit pricing the apps charge."
        right={
          <Badge tone={data?.editable ? 'ok' : 'neutral'}>
            {data?.editable ? (canManage ? 'editable' : 'read-only (no permission)') : 'read-only'}
          </Badge>
        }
      />

      {isLoading ? (
        <Card>
          <div className="flex items-center justify-center py-12">
            <Spinner label="Loading fare rates…" />
          </div>
        </Card>
      ) : isError ? (
        <Card>
          <EmptyState title="Couldn't load fare rates" description={(error as Error)?.message} />
        </Card>
      ) : data ? (
        <div className="space-y-4">
          {/* Headline km-credit knobs — GLOBAL + read-only (trip-svc owns them). */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile
              label="Buying rate"
              value={`$${data.buying_rate_per_km.toFixed(2)}/km`}
              hint="global · dollars → km credit"
              tone="info"
            />
            <StatTile
              label="Commission"
              value={`${data.admin_commission.toFixed(0)}%`}
              hint="global · platform take"
            />
            <StatTile label="Surge" value="None" hint="product decision" />
            <StatTile label="Fleet" value="Tesla-only" hint="single vehicle class" />
          </div>

          {/* Not-configured banner: the table is present but the trip_settings row
              has no buying rate yet. The table still renders (zeros read as "—"). */}
          {!data.configured ? (
            <Card>
              <div className="flex items-start gap-2.5 text-[13px] text-text-secondary">
                <IconReceipt className="mt-0.5 h-4 w-4 shrink-0 text-text-muted" />
                <p>
                  Rates aren’t configured yet — trip-svc’s <code className="metric text-xs">trip_settings</code>{' '}
                  row is unseeded, so the buying rate falls back to the deployment default and the per-km cells
                  below show “—”. They populate once pricing is seeded.
                </p>
              </div>
            </Card>
          ) : null}

          {/* The per-category rate table — the heart of the legacy resource. */}
          <Card flush>
            <div className="flex items-center justify-between gap-3 border-b border-ink-700 px-4 py-3">
              <div className="min-w-0">
                <h3 className="text-[13px] font-semibold text-slate-100">Per-category rates</h3>
                <p className="mt-0.5 text-xs text-text-secondary">
                  One row per trip type.{' '}
                  {data.rates_are_global
                    ? 'All categories share the global rate set.'
                    : 'Each category carries its own rates (inheriting the global set until edited).'}
                </p>
              </div>
              <TimeWindowLegend day={win?.day} evening={win?.evening} night={win?.night} />
            </div>
            <FareRatesTable rows={data.categories} canEdit={canEdit} onEdit={(c) => setEditing(c)} />
          </Card>

          {/* Honest read-only note — exactly why editing isn't wired (no dead
              button). Only shown when the page is NOT editable. */}
          {!data.editable && data.editable_blocked_reason ? (
            <Card>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">
                Editing fare rates
              </div>
              <p className="text-[13px] text-text-secondary">{data.editable_blocked_reason}</p>
              {data.source ? <p className="mt-2 text-xs text-text-muted">{data.source}</p> : null}
            </Card>
          ) : null}
        </div>
      ) : (
        <Card>
          <EmptyState title="No fare data" />
        </Card>
      )}

      {editing ? (
        <FareRateDrawer
          category={editing}
          timeWindows={data?.time_windows}
          buyingRate={data?.buying_rate_per_km}
          adminCommission={data?.admin_commission}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </AdminPage>
  );
}

/** The small Day / Evening / Night window legend shown in the table header, so the
 *  per-km column groups read unambiguously (matching the legacy field labels). */
function TimeWindowLegend({
  day,
  evening,
  night,
}: {
  day?: string;
  evening?: string;
  night?: string;
}) {
  if (!day && !evening && !night) return null;
  return (
    <div className="hidden shrink-0 items-center gap-3 text-[11px] text-text-muted sm:flex">
      {day ? <LegendItem label="Day" range={day} /> : null}
      {evening ? <LegendItem label="Eve" range={evening} /> : null}
      {night ? <LegendItem label="Night" range={night} /> : null}
    </div>
  );
}

function LegendItem({ label, range }: { label: string; range: string }) {
  return (
    <span className="whitespace-nowrap">
      <span className="font-semibold text-text-secondary">{label}</span>{' '}
      <span className="metric">{range}</span>
    </span>
  );
}

/** Format a per-km rate cell: a positive rate as "$x.xx", an unset (0) rate as a
 *  muted em-dash (the COALESCE on the server reads a NULL column as 0). */
function rate(v: number): ReactNode {
  if (!v || v <= 0) return <span className="text-text-muted">—</span>;
  return <span className="metric tabular-nums">${v.toFixed(2)}</span>;
}

/** The per-category rate DataTable — pulled out so the page body stays readable.
 *  Columns mirror the legacy FareRateResource table: Category, then the customer
 *  Day/Eve/Night per-km rates, then the driver Day/Eve/Night + flat sub-contractor
 *  rates, plus an Edit action (when canEdit). A subtle column grouping (customer
 *  vs driver) is conveyed by the header prefixes + the divider class. */
function FareRatesTable({
  rows,
  canEdit,
  onEdit,
}: {
  rows: FareCategory[];
  canEdit: boolean;
  onEdit: (c: FareCategory) => void;
}) {
  const columns: Array<Column<FareCategory>> = [
    {
      key: 'category',
      header: 'Category',
      cell: (r) => (
        <div className="flex items-center gap-2">
          <span className="grid h-7 w-7 place-items-center rounded-md bg-ink-850 text-text-muted ring-1 ring-inset ring-ink-700">
            <IconCar className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0">
            <div className="font-medium text-slate-100">{r.name || `Type ${r.id}`}</div>
            <div className="text-[11px] text-text-muted">
              #{r.id}
              {r.status === 0 ? ' · inactive' : ''}
              {r.rates_inherited ? ' · inherits global' : ''}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'cust_day',
      header: 'Cust day',
      align: 'right',
      className: 'border-l border-ink-800',
      cell: (r) => rate(r.rates.customer_rate_day),
    },
    { key: 'cust_eve', header: 'Cust eve', align: 'right', cell: (r) => rate(r.rates.customer_rate_evening) },
    { key: 'cust_night', header: 'Cust night', align: 'right', cell: (r) => rate(r.rates.customer_rate_night) },
    {
      key: 'drv_day',
      header: 'Drv day',
      align: 'right',
      className: 'border-l border-ink-800',
      cell: (r) => rate(r.rates.driver_rate_day),
    },
    { key: 'drv_eve', header: 'Drv eve', align: 'right', cell: (r) => rate(r.rates.driver_rate_evening) },
    { key: 'drv_night', header: 'Drv night', align: 'right', cell: (r) => rate(r.rates.driver_rate_night) },
    {
      key: 'drv_sub',
      header: 'Drv sub',
      align: 'right',
      className: 'border-l border-ink-800',
      cell: (r) => rate(r.rates.driver_rate_subcontractor),
    },
  ];

  if (canEdit) {
    columns.push({
      key: 'actions',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => onEdit(r)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          Edit
        </button>
      ),
    });
  }

  return (
    <DataTable<FareCategory>
      columns={columns}
      rows={rows}
      rowKey={(r) => String(r.id)}
      stickyHeader
      empty={
        <EmptyState
          title="No trip categories"
          description="The trip-type catalogue isn’t available."
          icon={<IconReceipt className="h-6 w-6" />}
        />
      }
    />
  );
}

// ─── per-category rate edit drawer ───────────────────────────

/** The editable form state — the 7 per-category rate columns, as strings so the
 *  number inputs bind cleanly (parsed + validated on submit). Mirrors the legacy
 *  Filament form's two sections (Customer per-km / Driver per-km). */
interface FormState {
  customer_rate_day: string;
  customer_rate_evening: string;
  customer_rate_night: string;
  driver_rate_day: string;
  driver_rate_evening: string;
  driver_rate_night: string;
  driver_rate_subcontractor: string;
}

/** FareRateDrawer — edit ONE category's time-banded customer + driver per-km
 *  rates. Seeded from that category's current `rates` (its own, or the inherited
 *  global block). On success it invalidates the table + closes. The category id
 *  + name are read-only context; the buying rate + commission are shown as a
 *  read-only "global" reminder (they are NOT edited here — trip-svc owns them).
 *
 *  RBAC: only finance + superadmin hold fares.manage; this drawer only ever opens
 *  for them (the page gates the Edit affordance on the same hook + data.editable).
 *  admin-svc enforces the matrix server-side and 403s/503s regardless. */
function FareRateDrawer({
  category,
  timeWindows,
  buyingRate,
  adminCommission,
  onClose,
}: {
  category: FareCategory;
  timeWindows?: { day: string; evening: string; night: string };
  buyingRate?: number;
  adminCommission?: number;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(() => seedForm(category.rates));
  const [clientError, setClientError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (input: FareRateInput) => adminApi.updateFareRate(category.id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'fare-rates'] });
      onClose();
    },
  });

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = () => {
    setClientError(null);
    const built = buildInput(form);
    if ('error' in built) {
      setClientError(built.error);
      return;
    }
    mutation.mutate(built.input);
  };

  const dayWin = timeWindows?.day ?? '06:00–18:00';
  const eveWin = timeWindows?.evening ?? '18:00–24:00';
  const nightWin = timeWindows?.night ?? '00:00–06:00';

  return (
    <Drawer
      title={`Edit rates — ${category.name || `Type ${category.id}`}`}
      subtitle="Per-category customer + driver per-km rates"
      onClose={onClose}
    >
      <div className="space-y-4">
        {/* Read-only context: category id + the GLOBAL knobs (not editable here). */}
        <Card>
          <Field label="Category" value={`${category.name || `Type ${category.id}`} · #${category.id}`} />
          <Field
            label="Buying rate (global)"
            value={buyingRate != null ? `$${buyingRate.toFixed(2)}/km` : '—'}
          />
          <Field
            label="Commission (global)"
            value={adminCommission != null ? `${adminCommission.toFixed(0)}%` : '—'}
          />
          {category.rates_inherited ? (
            <p className="mt-2 text-[11px] text-text-muted">
              This category has no per-category rates yet — the fields below are seeded from the global set.
              Saving creates this category’s own rates.
            </p>
          ) : null}
        </Card>

        {/* Customer per-km — the legacy "Customer per-km" section (3 cols). */}
        <Card>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
            Customer per-km (AUD)
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <RateField label={`Day (${dayWin})`} value={form.customer_rate_day} onChange={(v) => set('customer_rate_day', v)} />
            <RateField label={`Evening (${eveWin})`} value={form.customer_rate_evening} onChange={(v) => set('customer_rate_evening', v)} />
            <RateField label={`Night (${nightWin})`} value={form.customer_rate_night} onChange={(v) => set('customer_rate_night', v)} />
          </div>
        </Card>

        {/* Driver per-km — the legacy "Driver per-km" section (incl. sub-contractor). */}
        <Card>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
            Driver per-km (AUD)
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <RateField label={`Day (${dayWin})`} value={form.driver_rate_day} onChange={(v) => set('driver_rate_day', v)} />
            <RateField label={`Evening (${eveWin})`} value={form.driver_rate_evening} onChange={(v) => set('driver_rate_evening', v)} />
            <RateField label={`Night (${nightWin})`} value={form.driver_rate_night} onChange={(v) => set('driver_rate_night', v)} />
            <RateField label="Sub-contractor (flat)" value={form.driver_rate_subcontractor} onChange={(v) => set('driver_rate_subcontractor', v)} />
          </div>
        </Card>

        <div className="flex items-center gap-3">
          <button type="button" disabled={mutation.isPending} onClick={onSubmit} className="btn-primary">
            {mutation.isPending ? 'Saving…' : 'Save rates'}
          </button>
          <button type="button" disabled={mutation.isPending} onClick={onClose} className="btn-secondary">
            Cancel
          </button>
        </div>
        {clientError ? <p className="text-xs text-danger">{clientError}</p> : null}
        {mutation.isError ? <p className="text-xs text-danger">{(mutation.error as Error)?.message}</p> : null}
      </div>
    </Drawer>
  );
}

/** A labelled "$" rate input row used in the edit drawer. Numeric, non-negative;
 *  the leading "$" is decorative (the value is the bare number). */
function RateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-[11px] text-muted">{label}</label>
      <div className="mt-1 flex items-center">
        <span className="mr-1.5 text-sm text-text-muted">$</span>
        <input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="0.00"
          className="input"
        />
      </div>
    </div>
  );
}

/** Seed the form strings from a category's rate block. A 0 (unset/inherited)
 *  shows as an empty input so the staffer enters a deliberate value. */
function seedForm(r: FareRateBlock): FormState {
  const s = (v: number) => (v && v > 0 ? String(v) : '');
  return {
    customer_rate_day: s(r.customer_rate_day),
    customer_rate_evening: s(r.customer_rate_evening),
    customer_rate_night: s(r.customer_rate_night),
    driver_rate_day: s(r.driver_rate_day),
    driver_rate_evening: s(r.driver_rate_evening),
    driver_rate_night: s(r.driver_rate_night),
    driver_rate_subcontractor: s(r.driver_rate_subcontractor),
  };
}

/** Validate + convert the form into a FareRateInput, or return an error string.
 *  Every field is required + must be a finite, non-negative number within the
 *  NUMERIC(6,2) range (< 10000). Mirrors admin-svc validation so the staffer sees
 *  a precise message instantly; the server re-checks everything (UX nicety, not
 *  the boundary). An empty field is treated as 0 only if explicitly typed — a
 *  blank is rejected so a half-filled form can't silently zero a rate. */
function buildInput(f: FormState): { input: FareRateInput } | { error: string } {
  const fields: Array<[keyof FareRateBlock, string, string]> = [
    ['customer_rate_day', f.customer_rate_day, 'Customer day'],
    ['customer_rate_evening', f.customer_rate_evening, 'Customer evening'],
    ['customer_rate_night', f.customer_rate_night, 'Customer night'],
    ['driver_rate_day', f.driver_rate_day, 'Driver day'],
    ['driver_rate_evening', f.driver_rate_evening, 'Driver evening'],
    ['driver_rate_night', f.driver_rate_night, 'Driver night'],
    ['driver_rate_subcontractor', f.driver_rate_subcontractor, 'Driver sub-contractor'],
  ];
  const out = {} as FareRateBlock;
  for (const [key, raw, label] of fields) {
    const trimmed = raw.trim();
    if (trimmed === '') return { error: `${label} rate is required.` };
    const n = Number(trimmed);
    if (!Number.isFinite(n)) return { error: `${label} rate must be a number.` };
    if (n < 0) return { error: `${label} rate must be zero or greater.` };
    if (n >= 10000) return { error: `${label} rate is too large (max 9999.99).` };
    out[key] = n;
  }
  return { input: out };
}
